import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { ScheduledJobType } from '../../../api/campaignScheduledJobs';
import type { ScheduledJobItem } from './index';

interface ConfirmDeleteJobDialogProps {
  open: boolean;
  job: ScheduledJobItem | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function jobTypeLabel(jobType: ScheduledJobType): string {
  switch (jobType) {
    case 'campaign_prospect_ingestion':
      return 'CRM import';
    case 'campaign_cell_execution':
      return 'Cell enrichment';
    case 'campaign_crm_synchronization':
      return 'CRM sync (export)';
    default:
      return jobType;
  }
}

const ConfirmDeleteJobDialog: React.FC<ConfirmDeleteJobDialogProps> = ({
  open,
  job,
  deleting,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete scheduled job</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to delete this scheduled job?
          </Typography>
          {job && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {jobTypeLabel(job.jobType)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Schedule: {job.scheduleType || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Runs: {job.runCount}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            This cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={(theme) => ({
            border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
            backgroundColor: theme.customColors?.themeSecondary,
            color: theme.customColors?.themePrimary || theme.palette.primary.main,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:hover': {
              border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
              backgroundColor: theme.customColors?.themePrimary || theme.palette.primary.main,
              color: theme.customColors?.themeSecondary || 'white',
            },
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
          sx={(theme) => ({
            borderRadius: '50px',
            textTransform: 'capitalize',
          })}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteJobDialog;

